import * as auth from "@skilitics/auth-client";
import * as authWeb from "@skilitics/auth-client/web";

import getAuthClient from "../auth-client.ts";
import { mapAwsError } from "../error-internal.ts";

export async function logout() {
  authWeb.clearAuthStorage();
  await getAuthClient().logout();
}

const policyAttributeName = "custom:hasAcceptedPolicy";

export interface LoginStartOptions {
  staySignedIn?: boolean;
}

/**
 * Login using e.g. a username or password.
 *
 * Throws an AuthError for translating Cognito errors.
 */
export async function loginStart(
  usernameOrAlias: string,
  password: string,
  { staySignedIn }: LoginStartOptions = {},
): Promise<void> {
  const client = getAuthClient();
  try {
    await client
      .loginStart(usernameOrAlias, password, {
        store: staySignedIn ? authWeb.localAuthStore : authWeb.sessionAuthStore,
      })
      .catch(mapAwsError);
  } catch (error) {
    // we accept no-workspaces and allow persisting that state.
    if (!(error instanceof auth.AuthApiError && error.code === "no-workspaces")) {
      throw error;
    }
  }
}

export function hasAcceptedPolicy(): boolean {
  return getAuthClient().loginClaims?.[policyAttributeName] === "true";
}

export async function acceptPolicy() {
  await getAuthClient()
    .updateAttributes({
      [policyAttributeName]: "true",
    })
    .catch(mapAwsError);
}

export async function loginComplete(): Promise<void> {
  await getAuthClient().loginComplete().catch(mapAwsError);
}

// ========= sign up, forgot password ==========
// todo: Break out into separate modules.
// todo: Wrap up sign-up session?
//       Can handle the username internally then.

/**
 * Returns if users are allowed to sign up.
 * Assumed to be true currently.
 */
export function canSignUp(): boolean {
  return true;
}

/**
 * Options for {@link requestSignUp}.
 */
export interface SignUpOptions {
  /**
   * A username, should be a randomly generated uuid currently.
   * Chose this once before requesting sign up and re-use it if
   * the request fails to ensure the creation is idempotent.
   */
  readonly username: string;

  /**
   * The user's password. Must conform to the password
   * policy configured in the user pool.
   */
  readonly password: string;

  /**
   * Attributes to set on the user, must include at least
   * `email`, `given_name` and `family_name` for our user pool.
   */
  readonly attributes: {
    readonly email: string;
    readonly given_name: string;
    readonly family_name: string;
  };
}

/**
 * Begin the self-sign up process for a new user.
 * The user will receive a confirmation code by email,
 * which must be confirmed using {@link confirmSignUp}.
 *
 * @example
 * ```ts
 * import { requestSignUp, confirmSignUp } from "@skilitics/designer-auth/login";
 *
 * const username = crypto.randomUUID();
 * const email = prompt("Email:");
 * const given_name = prompt("First name:");
 * const family_name = prompt("Last name:");
 * const password = prompt("Password:");
 *
 * await requestSignUp({
 *   username,
 *   password,
 *   attributes: { email, given_name, family_name },
 * });
 * const code = prompt("Confirmation code sent to email:");
 * await confirmSignUp({ username, code });
 * ```
 *
 * @see resendSignUpCode
 * @see confirmSignUp
 */
export async function requestSignUp(options: SignUpOptions): Promise<void> {
  await getAuthClient().requestSignUp(options.username, options.password, options.attributes).catch(mapAwsError);
}

/**
 * When a user has not received or has lost their sign-up confirmation code,
 * this can be used to resend the code to the user's `email`.
 * @see requestSignUp
 * @see confirmSignUp
 */
export async function resendSignUpCode(username: string): Promise<void> {
  await getAuthClient().resendSignUpCode(username).catch(mapAwsError);
}

/**
 * Options for {@link confirmSignUp}.
 */
export interface ConfirmSignUpOptions {
  readonly username: string;
  readonly code: string;
}

/**
 * Confirm a new user sign up using the confirmation code sent
 * to the user's email by either {@link requestSignUp} or
 * {@link resendSignUpCode}.
 */
export async function confirmSignUp(options: ConfirmSignUpOptions): Promise<void> {
  await getAuthClient().confirmSignUp(options.username, options.code).catch(mapAwsError);
}

/**
 * Begin the password reset flow for a user. The user will receive
 * a confirmation code by email, which must be confirmed using
 * {@link confirmResetPassword}.
 */
export async function requestResetPassword(usernameOrAlias: string): Promise<void> {
  await getAuthClient().requestResetPassword(usernameOrAlias).catch(mapAwsError);
}

/**
 * Options for {@link confirmResetPassword}.
 */
export interface ConfirmResetPasswordOptions {
  /**
   * The user's username, or an alias such as their email.
   */
  readonly usernameOrAlias: string;

  /**
   * The confirmation code sent to the user's email by
   * {@link requestResetPassword}.
   */
  readonly code: string;

  /**
   * The new password for the user. Must conform to the
   * password policy configured in the user pool.
   */
  readonly newPassword: string;
}

/**
 * Complete the password reset flow using the confirmation code
 * sent to the user's email by {@link requestResetPassword}.
 */
export async function confirmResetPassword(options: ConfirmResetPasswordOptions): Promise<void> {
  await getAuthClient()
    .confirmResetPassword({
      usernameOrAlias: options.usernameOrAlias,
      code: options.code,
      password: options.newPassword,
    })
    .catch(mapAwsError);
}
